import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { ApiImage } from '../../components/ui/ApiImage';
import { num } from '../../format';
import { ProfilePrinterPanel } from './ProfilePrinterPanel';
import { ConsumedNote } from './ConsumedNote';
import { loadLastTemplate, saveLastTemplate } from './printerProfile';
import {
  calibrationPdf,
  enqueue,
  listTemplates,
  previewUrl,
  printBlob,
  renderPdf,
  reportPrinted,
  templateStock,
  updateTemplate,
  type Consumed,
  type LabelTemplate,
  type SheetMode,
  type TemplateChanges,
} from './api';

interface Props {
  entityKind: 'component' | 'location' | 'serial' | 'package';
  entityId: string;
  onClose: () => void;
}

function templateName(template: LabelTemplate, language: string): string {
  return language.startsWith('en') ? template.name_en : template.name_ru;
}

/**
 * Print one label, or put it in the queue for the machine that can print it.
 *
 * The preview is not decoration. A label is consumable and a thermal roll is
 * bought by the metre; discovering on screen that a part name came out
 * ellipsised costs nothing, and discovering it on the twentieth printed label
 * costs the roll and the time spent sticking them on.
 */
export function PrintDialog({ entityKind, entityId, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [templateId, setTemplateId] = useState(() => loadLastTemplate(entityKind));
  const [copiesInput, setCopiesInput] = useState('1');
  const copies = Math.min(200, Math.max(1, Number(copiesInput) || 1));
  const [mode, setMode] = useState<SheetMode>('labels');
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  // Asked after the browser's own print dialog has been and gone. Whether paper
  // came out is not something a web page can observe, and it is the only thing
  // that decides whether the roll got shorter.
  const [confirming, setConfirming] = useState(false);
  const [spent, setSpent] = useState<Consumed | null>(null);
  const [showAll, setShowAll] = useState(false);

  const templates = useQuery({
    // Narrowed by the record: the server drops templates whose blacklist names
    // this part's type, so a 12 mm tape is not offered for an enclosure.
    queryKey: ['labels', 'templates', entityKind, showAll ? null : entityId],
    queryFn: () => listTemplates(entityKind, showAll ? undefined : entityId),
  });

  const stock = useQuery({
    queryKey: ['labels', 'templates', templateId, 'stock'],
    queryFn: () => templateStock(templateId),
    enabled: Boolean(templateId),
  });

  useEffect(() => {
    const list = templates.data ?? [];
    if (list.some((item) => item.id === templateId)) return;
    const preferred = list.find((item) => item.is_default) ?? list[0];
    if (preferred) { setTemplateId(preferred.id); saveLastTemplate(entityKind, preferred.id); }
  }, [entityKind, templates.data, templateId]);

  const template = templates.data?.find((item) => item.id === templateId) ?? null;

  const toPrinter = useMutation({
    mutationFn: async () => {
      const blob = await renderPdf({
        template_id: templateId,
        entity_ids: [entityId],
        copies,
        mode,
      });
      printBlob(blob);
    },
    onSuccess: () => {
      setError(null);
      setSpent(null);
      // Only for the roll. A sheet of A4 is not the consumable the template
      // names, and asking about it would be a question with a wrong answer.
      if (mode === 'labels' && stock.data?.component_id) setConfirming(true);
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const report = useMutation({
    mutationFn: () => reportPrinted(templateId, copies),
    onSuccess: (result) => {
      setConfirming(false);
      setSpent(result);
      void queryClient.invalidateQueries({ queryKey: ['labels', 'templates'] });
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const toQueue = useMutation({
    mutationFn: () => enqueue({ template_id: templateId, entity_id: entityId, copies }),
    onSuccess: () => {
      setError(null);
      setQueued(true);
      void queryClient.invalidateQueries({ queryKey: ['labels', 'queue'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const calibrate = useMutation({
    mutationFn: async () => printBlob(await calibrationPdf(templateId)),
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const turn = useMutation({
    mutationFn: (changes: TemplateChanges) => updateTemplate(templateId, changes),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['labels', 'templates'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  return (
    <div className="print-dialog">
      <div className="print-dialog__head">
        <h3>{t('labels.print')}</h3>
        <button type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>

      <label>
        {t('labels.template')}
        <select value={templateId} onChange={(event) => { setTemplateId(event.target.value); saveLastTemplate(entityKind, event.target.value); setCopiesInput('1'); }}>
          {(templates.data ?? []).map((item) => (
            <option key={item.id} value={item.id}>
              {templateName(item, i18n.language)} · {item.media_width_mm}×{item.media_height_mm} {t('labels.mm')}
            </option>
          ))}
        </select>
      </label>

      {stock.data?.component_id && stock.data.remaining !== null && (
        <p className="hint">
          {t('labels.stock.remaining', { n: num(stock.data.remaining) })}
          {mode === 'labels' && ` → ${num(String(Number(stock.data.remaining) - copies))}`}
        </p>
      )}      {/* Every template excluded this part's type. An empty dropdown with no
          explanation is the worst possible answer, so it says so and offers the
          full list — the blacklist is a convenience, not a prohibition. */}
      {templates.data?.length === 0 && !showAll && (
        <p className="panel__hint">
          {t('labels.noneForType')}{' '}
          <button type="button" className="linklike" onClick={() => setShowAll(true)}>
            {t('labels.showAll')}
          </button>
        </p>
      )}

      {template && (
        <div className="print-dialog__preview">
          <ApiImage
            path={previewUrl(template.id, entityId)}
            alt={t('labels.preview')}
            style={{
              // Shown at its true proportions and roughly its true size, so the
              // preview answers "will this fit on the box" and not just "does it
              // render".
              aspectRatio: `${template.media_width_mm} / ${template.media_height_mm}`,
              width: `${Math.min(template.media_width_mm * 4, 320)}px`,
            }}
          />
          <p className="hint">
            {template.media_width_mm}×{template.media_height_mm} {t('labels.mm')} · {template.dpi} dpi
          </p>
        </div>
      )}

      {template && <ProfilePrinterPanel templateId={templateId} entityId={entityId} copies={copies} medium={{ widthMm: template.media_width_mm, heightMm: template.media_height_mm }} systemBusy={toPrinter.isPending} onSystemPrint={() => toPrinter.mutate()} onDirectPrinted={() => report.mutateAsync().then(() => undefined)} compact />}
      <div className="print-dialog__row">
        <label>
          {t('labels.copies')}
          <span className="quantity-stepper">
            <button type="button" onClick={() => setCopiesInput(String(Math.max(1, copies - 1)))} aria-label={t('common.decrease')}>{String.fromCharCode(8722)}</button>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={200}
              value={copiesInput}
              onChange={(event) => setCopiesInput(event.target.value)}
              onBlur={() => setCopiesInput(String(copies))}
            />
            <button type="button" onClick={() => setCopiesInput(String(Math.min(200, copies + 1)))} aria-label={t('common.increase')}>+</button>
          </span>
        </label>

        {template?.target === 'pdf' && <label>
          {t('labels.mode')}
          <select value={mode} onChange={(event) => setMode(event.target.value as SheetMode)}>
            <option value="labels">{t('labels.modeLabels')}</option>
            <option value="sheet">{t('labels.modeSheet')}</option>
          </select>
        </label>}
      </div>

      {/* How many labels this run leaves behind. Quiet, next to the count that
          decides it — a roll that runs out mid-batch is discovered here or at
          the printer, and here is cheaper. */}


      {error && <p className="error">{t(`labels.errors.${error}`, t('labels.errors.unknown'))}</p>}
      {queued && <p className="hint">{t('labels.queued')}</p>}
      {spent && <ConsumedNote consumed={spent} />}

      {/* The browser hands the PDF to a dialog and stops knowing anything. The
          same question the print station asks, for the same reason. */}
      {confirming && (
        <div className="print-dialog__confirm">
          <p>{t('labels.didItPrint', { count: copies })}</p>
          <button type="button" disabled={report.isPending} onClick={() => report.mutate()}>
            {t('labels.printedYes')}
          </button>
          <button type="button" onClick={() => setConfirming(false)}>
            {t('labels.printedNoPlain')}
          </button>
        </div>
      )}

      {/* Tape templates have their own direct Bluetooth action above. Do not
          offer the PDF fallback here under the same "Print now" caption: it
          opens Windows' print dialog and looks like a second MarkLife action. */}
      <div className="print-dialog__actions">
        <button
          type="button"
          disabled={!templateId || toQueue.isPending}
          onClick={() => toQueue.mutate()}
        >
          {t('labels.toQueue')}
        </button>
      </div>

      <details className="print-dialog__calibrate"><summary>{t('labels.printer.diagnostics')}</summary><p className="hint">{t('labels.calibrateHint')}</p><button type="button" className="linklike" disabled={!templateId} onClick={() => calibrate.mutate()}>{t('labels.calibrate')}</button></details>

      {/* The escape hatch for a driver that turns the page. It is stored on the
          template rather than remembered per click, because the answer is a
          property of the printer and applies to the queue as well. */}
      {template && (
        <div className="print-dialog__rotate">
          <label>
            {t('labels.rotate')}
            <select
              value={template.rotate}
              onChange={(event) =>
                turn.mutate({ rotate: Number(event.target.value) })
              }
            >
              {[0, 90, 180, 270].map((angle) => (
                <option key={angle} value={angle}>
                  {angle}°
                </option>
              ))}
            </select>
          </label>
          <span className="hint">{t('labels.rotateHint')}</span>
        </div>
      )}
    </div>
  );
}
